import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    QueryList,
    TemplateRef,
    TrackByFunction,
    ViewChild,
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { AppTableTemplateDirective } from './table-templates.directive';
import {
    AppTablePageEvent,
    AppTableRowSelectEvent,
    AppTableRowUnselectEvent,
    AppTableSortEvent,
    TableSortOrder,
} from './table.types';

@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AppTableComponent<T = unknown> implements AfterContentInit, OnChanges {
    @Input() value: T[] | null = null;

    /** Prime-like root styles/classes */
    @Input() style: Record<string, unknown> | null = null;
    @Input() styleClass: string | null = null;

    /** Prime-like table element styles/classes */
    @Input() tableStyle: Record<string, unknown> | null = null;
    @Input() tableStyleClass: string | null = null;

    /** Prime scroll options */
    @Input() scrollable = false;
    @Input() scrollHeight: string | null = null;

    /** Pagination (Prime-like subset) */
    @Input() paginator = false;
    @Input() first: number | null = null;
    @Input() rows: number | null = null;
    @Input() totalRecords: number | null = null;
    @Input() rowsPerPageOptions: number[] | null = null;
    @Input() pageLinks = 5;
    @Input() showCurrentPageReport = false;
    @Input() currentPageReportTemplate: string | null = null;
    @Output() onPage = new EventEmitter<AppTablePageEvent>();

    /** Sorting (Prime-like subset) */
    @Input() sortField: string | null = null;
    @Input() sortOrder: TableSortOrder = 0;
    @Input() sortMode: 'single' | 'multiple' = 'single';
    @Output() onSort = new EventEmitter<AppTableSortEvent>();

    /** Selection (Prime-like subset) */
    @Input() selectionMode: 'single' | 'multiple' | null = null;
    @Input() selection: T | T[] | null = null;
    @Output() selectionChange = new EventEmitter<T | T[] | null>();
    @Input() dataKey: string | null = null;
    @Input() metaKeySelection = false;
    @Output() onRowSelect = new EventEmitter<AppTableRowSelectEvent<T>>();
    @Output() onRowUnselect = new EventEmitter<AppTableRowUnselectEvent<T>>();

    @ViewChild(MatPaginator) private readonly matPaginator?: MatPaginator;

    @ContentChildren(AppTableTemplateDirective) private readonly templates?: QueryList<AppTableTemplateDirective>;

    captionTemplate?: TemplateRef<unknown>;
    headerTemplate?: TemplateRef<unknown>;
    bodyTemplate?: TemplateRef<unknown>;
    emptyMessageTemplate?: TemplateRef<unknown>;
    footerTemplate?: TemplateRef<unknown>;
    summaryTemplate?: TemplateRef<unknown>;

    readonly trackByIdentity: TrackByFunction<T> = (_index: number, item: T) => item as any;

    private firstInternal = 0;
    /** Exposed for template bindings (e.g. mat-paginator). */
    rowsInternal = 10;

    ngOnChanges(): void {
        // Keep internal paginator state compatible with Prime-like controlled usage.
        this.firstInternal = Math.max(0, this.first ?? this.firstInternal ?? 0);
        this.rowsInternal = Math.max(1, this.rows ?? this.rowsInternal ?? 10);
        this.clampFirstToBounds();
    }

    ngAfterContentInit(): void {
        this.hydrateTemplates();
        this.templates?.changes.subscribe(() => this.hydrateTemplates());
    }

    get allRows(): T[] {
        return this.value ?? [];
    }

    get rootClassList(): Record<string, boolean> {
        return {
            'p-datatable': true,
            'p-component': true,
            'p-datatable-scrollable': this.scrollable,
            'p-datatable-paginator': this.paginator,
        };
    }

    get wrapperStyle(): Record<string, unknown> | null {
        if (!this.scrollable) return null;

        if (!this.scrollHeight) {
            return { overflow: 'auto' };
        }

        if (this.scrollHeight === 'flex') {
            return { overflow: 'auto', flex: '1 1 auto' };
        }

        // Prime uses a fixed viewport height for vertical scrolling.
        return { overflow: 'auto', height: this.scrollHeight };
    }

    get processedRows(): T[] {
        const data = this.allRows;
        if (!this.sortField || this.sortOrder === 0) return data;

        const field = this.sortField;
        const order = this.sortOrder;
        // Avoid mutating input arrays.
        return [...data].sort((a, b) => this.compareByField(a, b, field) * order);
    }

    get totalRecordsResolved(): number {
        return this.totalRecords ?? this.processedRows.length;
    }

    get displayedRows(): T[] {
        if (!this.paginator) return this.processedRows;
        return this.processedRows.slice(this.firstInternal, this.firstInternal + this.rowsInternal);
    }

    get pageCount(): number {
        return Math.max(1, Math.ceil(this.totalRecordsResolved / this.rowsInternal));
    }

    get currentPage(): number {
        return Math.floor(this.firstInternal / this.rowsInternal);
    }

    get currentPageReportText(): string {
        const tpl =
            this.currentPageReportTemplate ??
            'Showing {first} to {last} of {totalRecords} entries';

        const total = this.totalRecordsResolved;
        const first = total === 0 ? 0 : this.firstInternal + 1;
        const last = Math.min(this.firstInternal + this.rowsInternal, total);

        return tpl
            .replace('{first}', String(first))
            .replace('{last}', String(last))
            .replace('{rows}', String(this.rowsInternal))
            .replace('{totalRecords}', String(total))
            .replace('{currentPage}', String(this.currentPage + 1))
            .replace('{totalPages}', String(this.pageCount));
    }

    get pageLinkNumbers(): number[] {
        const totalPages = this.pageCount;
        const maxLinks = Math.max(1, this.pageLinks || 5);
        const current = this.currentPage;

        const half = Math.floor(maxLinks / 2);
        let start = Math.max(0, current - half);
        const end = Math.min(totalPages - 1, start + maxLinks - 1);
        start = Math.max(0, end - maxLinks + 1);

        const pages: number[] = [];
        for (let p = start; p <= end; p++) pages.push(p);
        return pages;
    }

    onMatPage(event: PageEvent): void {
        this.firstInternal = event.pageIndex * event.pageSize;
        this.rowsInternal = event.pageSize;
        this.emitPage();
    }

    goToPage(pageIndex: number): void {
        if (!this.paginator) return;
        const clamped = Math.min(Math.max(0, pageIndex), this.pageCount - 1);
        this.firstInternal = clamped * this.rowsInternal;
        this.emitPage();
    }

    goToFirstPage(): void {
        this.goToPage(0);
    }

    goToPreviousPage(): void {
        this.goToPage(this.currentPage - 1);
    }

    goToNextPage(): void {
        this.goToPage(this.currentPage + 1);
    }

    goToLastPage(): void {
        this.goToPage(this.pageCount - 1);
    }

    toggleSort(field: string, originalEvent: Event): void {
        if (!field) return;

        if (this.sortMode !== 'single') {
            // Multi-sort not implemented (yet). Keep API surface safe.
            this.sortMode = 'single';
        }

        if (this.sortField !== field) {
            this.sortField = field;
            this.sortOrder = 1;
        } else {
            this.sortOrder = this.sortOrder === 1 ? -1 : this.sortOrder === -1 ? 0 : 1;
            if (this.sortOrder === 0) this.sortField = null;
        }

        this.onSort.emit({ field: this.sortField, order: this.sortOrder });
        if (this.paginator) {
            // Prime resets to first page on sort (single sort mode).
            this.firstInternal = 0;
            this.emitPage();
        }
    }

    isRowSelected(row: T): boolean {
        if (!this.selectionMode) return false;

        if (this.selectionMode === 'single') {
            const selected = this.selection as T | null;
            return selected ? this.equalsByKey(selected, row) : false;
        }

        const selected = (this.selection as T[] | null) ?? [];
        return selected.some((s) => this.equalsByKey(s, row));
    }

    toggleRowSelection(row: T, index: number, originalEvent: MouseEvent): void {
        if (!this.selectionMode) return;

        const metaKeyPressed = (originalEvent as MouseEvent).metaKey || (originalEvent as MouseEvent).ctrlKey;

        if (this.selectionMode === 'single') {
            const isSelected = this.isRowSelected(row);
            const shouldUnselect = isSelected && (!this.metaKeySelection || metaKeyPressed);
            const nextSelection = shouldUnselect ? null : row;
            this.selection = nextSelection;
            this.selectionChange.emit(nextSelection);

            if (shouldUnselect) {
                this.onRowUnselect.emit({ originalEvent, data: row, index, type: 'row' });
            } else {
                this.onRowSelect.emit({ originalEvent, data: row, index, type: 'row' });
            }
            return;
        }

        const current = ((this.selection as T[] | null) ?? []).slice();
        const isSelected = current.some((s) => this.equalsByKey(s, row));
        let next: T[];

        if (isSelected) {
            next = current.filter((s) => !this.equalsByKey(s, row));
            this.onRowUnselect.emit({ originalEvent, data: row, index, type: 'row' });
        } else {
            next = current.concat([row]);
            this.onRowSelect.emit({ originalEvent, data: row, index, type: 'row' });
        }

        this.selection = next;
        this.selectionChange.emit(next);
    }

    bodyContext(row: T, rowIndex: number): Record<string, unknown> {
        return { $implicit: row, rowIndex };
    }

    private emitPage(): void {
        this.clampFirstToBounds();
        this.onPage.emit({
            first: this.firstInternal,
            rows: this.rowsInternal,
            page: this.currentPage,
            pageCount: this.pageCount,
        });

        // Keep the internal MatPaginator state in sync when it exists.
        if (this.matPaginator) {
            this.matPaginator.pageIndex = this.currentPage;
            this.matPaginator.pageSize = this.rowsInternal;
            this.matPaginator.length = this.totalRecordsResolved;
        }
    }

    private clampFirstToBounds(): void {
        const total = this.totalRecordsResolved;
        if (total <= 0) {
            this.firstInternal = 0;
            return;
        }
        const maxFirst = Math.max(0, (this.pageCount - 1) * this.rowsInternal);
        this.firstInternal = Math.min(Math.max(0, this.firstInternal), maxFirst);
    }

    private equalsByKey(a: T, b: T): boolean {
        if (!this.dataKey) return a === b;
        const ak = this.resolveFieldData(a as any, this.dataKey);
        const bk = this.resolveFieldData(b as any, this.dataKey);
        return ak === bk;
    }

    private compareByField(a: T, b: T, field: string): number {
        const av = this.resolveFieldData(a as any, field);
        const bv = this.resolveFieldData(b as any, field);
        return this.compareValues(av, bv);
    }

    private resolveFieldData(data: any, field: string): any {
        if (!data || !field) return null;
        if (field.indexOf('.') === -1) return data[field];
        return field.split('.').reduce((obj: any, key: string) => (obj ? obj[key] : null), data);
    }

    private compareValues(a: any, b: any): number {
        if (a == null && b == null) return 0;
        if (a == null) return -1;
        if (b == null) return 1;

        // Dates
        if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();

        // Numbers
        if (typeof a === 'number' && typeof b === 'number') return a - b;

        // Booleans
        if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);

        // Fallback to string compare
        return String(a).localeCompare(String(b));
    }

    private hydrateTemplates(): void {
        const byName = new Map<string, TemplateRef<unknown>>();
        for (const t of this.templates?.toArray() ?? []) {
            const name = (t.name || '').trim();
            if (!name) continue;
            byName.set(name, t.template);
        }

        this.captionTemplate = byName.get('caption') ?? undefined;
        this.headerTemplate = byName.get('header') ?? undefined;
        this.bodyTemplate = byName.get('body') ?? undefined;
        this.emptyMessageTemplate = byName.get('emptymessage') ?? undefined;
        this.footerTemplate = byName.get('footer') ?? undefined;
        this.summaryTemplate = byName.get('summary') ?? undefined;

        // Support the shorthand Prime uses in docs sometimes (`#header`, `#body`) by allowing those names too.
        this.captionTemplate ??= byName.get('#caption') ?? undefined;
        this.headerTemplate ??= byName.get('#header') ?? undefined;
        this.bodyTemplate ??= byName.get('#body') ?? undefined;
        this.emptyMessageTemplate ??= byName.get('#emptymessage') ?? undefined;
        this.footerTemplate ??= byName.get('#footer') ?? undefined;
        this.summaryTemplate ??= byName.get('#summary') ?? undefined;
    }
}

